/**
 * Minimal localStorage-backed reactive store.
 *
 * - `get()`      returns the current state object (treat as read-only)
 * - `set(patch)` shallow-merges an object, or accepts `(state) => newState`
 * - `subscribe(fn)` registers a listener, returns an unsubscribe function
 *
 * State is persisted to localStorage synchronously on every `set`, so a
 * reload restores exactly what the user last saw. Reads are synchronous,
 * which is why grid badges can be painted without awaiting anything.
 */
export function createStore(key, initial) {
  const subscribers = new Set();

  function load() {
    try {
      const raw = localStorage.getItem(key);
      return raw ? { ...initial, ...JSON.parse(raw) } : { ...initial };
    } catch {
      return { ...initial };
    }
  }

  let state = load();

  function save() {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      /* private mode / quota — keep working in-memory */
    }
  }

  return {
    get: () => state,
    set(patch) {
      state = typeof patch === 'function' ? patch(state) : { ...state, ...patch };
      save();
      for (const fn of subscribers) fn(state);
    },
    subscribe(fn) {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },
    /** Replace the whole state (used by data import). */
    replace(next) {
      state = { ...initial, ...next };
      save();
      for (const fn of subscribers) fn(state);
    },
  };
}
